import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "@/lib/site";
import logo from "@/assets/logo-mark.png";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-brand-bg/85 backdrop-blur-md shadow-[0_1px_0_rgba(27,79,156,0.08)]"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="flex h-20 items-center justify-between gap-4">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2 shrink-0">
            <img src={logo} alt="Fly Gowell Travel Solutions" className="h-12 w-auto object-contain" />
            <span className="leading-tight">
  <span className="block text-base font-bold text-brand-blue">FlyGowell</span>
  <span className="block text-[10px] font-medium tracking-wider text-brand-slate uppercase">Travel Solutions</span>
</span>
          </Link>

          <nav className="hidden xl:flex items-center gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium tracking-tight transition-colors ${
                    isActive ? "text-brand-blue" : "text-brand-slate hover:text-brand-ocean"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              data-testid="header-get-in-touch"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-brand-blue px-5 py-2.5 text-sm font-medium text-white transition-all hover:bg-brand-ocean hover:gap-2.5"
            >
              Get in Touch <ArrowUpRight size={16} />
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
              onClick={() => setOpen((v) => !v)}
              className="xl:hidden p-2 text-brand-blue"
            >
              {open ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div data-testid="mobile-menu" className="xl:hidden bg-brand-bg/95 backdrop-blur-md border-t border-brand-blue/10">
          <nav className="mx-auto max-w-[1400px] px-5 py-4 flex flex-col">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                className={({ isActive }) =>
                  `py-3 text-base font-medium border-b border-brand-blue/5 ${
                    isActive ? "text-brand-blue" : "text-brand-slate"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              className="mt-4 inline-flex justify-center rounded-full bg-brand-blue px-5 py-3 text-sm font-medium text-white"
            >
              Get in Touch
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
